import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";

import { ApiError, isPlanLimit } from "./api";
import { orgIdFromPath } from "./billing";
import { showPlanLimitToast } from "./notify";
import { noteSuspension } from "./suspension";

declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: {
      /** the screen shows PLAN_LIMIT errors inline, so skip the global toast */
      planLimitInline?: boolean;
    };
  }
}

export const queryClient: QueryClient = new QueryClient({
  // A suspended workspace answers 403 WORKSPACE_SUSPENDED to every org-scoped
  // request; the app shell then shows the suspended state instead of broken pages.
  queryCache: new QueryCache({
    onError: (err, query) => {
      noteSuspension(err, query.queryKey, window.location.pathname);
    },
  }),
  mutationCache: new MutationCache({
    // Runs before the per-call onError handlers. Those use toastError(), which
    // shows the same toast id, so a plan limit produces exactly one toast.
    onError: (err, _vars, _ctx, mutation) => {
      if (noteSuspension(err, undefined, window.location.pathname)) return;
      if (!isPlanLimit(err)) return;

      // usage is out of date when the server says a limit was hit
      const orgId = orgIdFromPath(window.location.pathname);
      if (orgId) void queryClient.invalidateQueries({ queryKey: ["org", orgId, "billing"] });

      if (!mutation.meta?.planLimitInline) showPlanLimitToast(err);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      retry: (count, err) => {
        // never retry client errors
        if (err instanceof ApiError && err.status >= 400 && err.status < 500) return false;

        return count < 2;
      },
    },
    mutations: { retry: false },
  },
});
